import { OpenAPI as OpenAPICollCalc } from '@festina/collcalc-general-types/collcalc'
import { OpenAPI as OpenAPIConfiguration } from '@festina/configuration-general-types/configuration'
import { OpenAPI as OpenAPILegalEntity } from '@festina/legalentity-general-types/legalentity'
import { OpenAPI as OpenAPILife } from '@festina/life-general-types/life'
import { OpenAPI as OpenAPIMoneyIn } from '@festina/moneyin-general-types/moneyin'
import { OpenAPI as OpenAPIMoneyOut } from '@festina/moneyout-general-types/moneyout'
import { OpenAPI as OpenAPIUnitlink } from '@festina/unitlink-general-types/unitlink'
import type { ConfigurableServer } from '@festina-life/core'
import type { WorkerMapping } from '@festina-life/fplus-monaco'

import type { ITextModel, LanguageServerInput } from '../monaco/types/monaco'

export const asWorkerInput: WorkerMapping<LanguageWorkerInput> = async (
  inputData,
) => {
  const model = inputData.model as ITextModel

  if (!model.languageServerInput) {
    return null
  }

  const hoverDisabled = !model.ctrlIsPressed && model.hoverOnCtrl

  return {
    textTransformer: model.textTransformer,
    data: {
      ...model.languageServerInput,
      expressions: model.getValue(),
      caret: {
        _type: 'FPlusCaretPosition',
        lineNumber: inputData.position?.lineNumber ?? 0,
        column: inputData.position?.column ?? 0,
      },
      providerOptions: {
        hoverDisabled: !!hoverDisabled,
      },
      apiContext: await baseFromContext(model),
    },
  }
}

// @beta temporary solution to fix constant token fetching until we figure out why we fetch so often
let latestModelTokens: {
  id: string
  tokens: {
    config: string
    life: string
    legalEntity: string
    unitLink: string
    moneyIn: string
    moneyOut: string
    collCalc: string
  }
} | null = null

export type LanguageWorkerInput = LanguageServerInput & {
  apiContext: BaseFromContextType
}

export const baseFromContext = async (
  model: ITextModel,
): Promise<BaseFromContextType> => {
  if (model.id !== latestModelTokens?.id) {
    latestModelTokens = {
      id: model.id,
      tokens: {
        config: await getToken(OpenAPIConfiguration),
        life: await getToken(OpenAPILife),
        legalEntity: await getToken(OpenAPILegalEntity),
        unitLink: await getToken(OpenAPIUnitlink),
        moneyIn: await getToken(OpenAPIMoneyIn),
        moneyOut: await getToken(OpenAPIMoneyOut),
        collCalc: await getToken(OpenAPICollCalc),
      },
    }
  }

  return {
    config: {
      token: latestModelTokens.tokens!.config,
      base: OpenAPIConfiguration.BASE,
    },
    life: { token: latestModelTokens.tokens!.life, base: OpenAPILife.BASE },
    legalEntity: {
      token: latestModelTokens.tokens!.legalEntity,
      base: OpenAPILegalEntity.BASE,
    },
    unitLink: {
      token: latestModelTokens.tokens!.unitLink,
      base: OpenAPIUnitlink.BASE,
    },
    moneyIn: {
      token: latestModelTokens.tokens!.moneyIn,
      base: OpenAPIMoneyIn.BASE,
    },
    moneyOut: {
      token: latestModelTokens.tokens!.moneyOut,
      base: OpenAPIMoneyOut.BASE,
    },
    collCalc: {
      token: latestModelTokens.tokens!.collCalc,
      base: OpenAPICollCalc.BASE,
    },
  }
}

export type BaseFromContextType = ConfigContextType &
  Record<
    ConfigurableServer,
    {
      token: string
      base: string
    }
  >

type ConfigContextType = { config: { token: string; base: string } }

const getToken = async (api: typeof OpenAPIConfiguration) => {
  if (api.TOKEN) {
    return typeof api.TOKEN === 'string'
      ? api.TOKEN
      : await api.TOKEN({
          method: 'GET',
          url: '',
        })
  } else {
    return ''
  }
}
